import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Button, Form, Spinner } from "react-bootstrap";
import { CountdownTimer } from "../../components/Quiz/CountdownTimer";
import { CreateQuestion } from "../../components/Quiz/CreateQuestion";
import { toast } from "react-toastify";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faSquare } from "@fortawesome/free-regular-svg-icons";
import { faCircleCheck, faSquareCheck } from "@fortawesome/free-solid-svg-icons";

import { TOASTIFY_OPTIONS } from "../../config/ReactToastify";
import { useAppStore } from "../../providers/AppStoreProvider";
import { useAPI } from "../../providers/ApiProvider";
import { toastWithAsyncFetch } from "../../utils/helper";

export const Result = ({ result, isLoading = false, setAction }) => {
  const store = useAppStore();

  const percent = useMemo(
    () =>
      (result.score === 0
        ? 0
        : (result.score / store.currentLesson.quiz.total_score) * 100
      ).toFixed(),
    [result],
  );

  return (
    <div className="quiz-alert quiz mt-4 d-flex flex-column justify-content-between align-items-center">
      <div className="alert text-center pt-4">
        <div className={(result.isPassed ? "passed " : "warning ") + "mb-4"}>
          <i
            className={
              (result.isPassed ? "fa-circle-check " : "fa-triangle-exclamation ") +
              "fa-duotone fa-3x"
            }
          ></i>
        </div>
        <div className="fs-5 fw-bold">
          {result.isPassed ? "Yay! Bạn đã hoàn thành bài quiz này!" : "Hãy thử lại nhé"}
        </div>
        <div>
          Kết quả gần nhất:{" "}
          <span className={(result.isPassed ? "passed " : "warning ") + "fw-bold"}>{percent}%</span>
        </div>
      </div>
      <Button
        className="try-again-btn btn rounded-pill border-0 px-4 my-3"
        onClick={() => setAction("see")}
      >
        {isLoading ? (
          <Spinner animation="border" size="sm" className="me-2" />
        ) : (
          <i className="fa-solid fa-eye me-2"></i>
        )}
        Xem kết quả
      </Button>

      <div role="button" className="text-underline mb-4" onClick={() => setAction("redo")}>
        Làm lại
      </div>
    </div>
  );
};

const Alert = ({ isPassed, setAction }) => {
  const store = useAppStore();

  const percent = useMemo(() => {
    const total_score = store.currentLesson.quiz.total_score;
    const result = store.currentLesson.quiz.userQuiz.score;

    return Number((result / total_score) * 100).toFixed();
  }, [store]);

  return (
    <div className="quiz-alert d-flex flex-row align-items-center justify-content-between rounded-10 p-4 mb-4">
      <div className=" d-flex flex-row align-items-center">
        {isPassed ? (
          <>
            <div className="passed me-4">
              <i className="fa-duotone fa-circle-check fa-3x"></i>
            </div>
            <div>
              <div className="fs-5 fw-bold">Yay! Bạn đã hoàn thành bài quiz này!</div>
              <div>
                Kết quả: <span className="passed fw-bold">{percent}%</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="warning me-4">
              <i className="fa-duotone fa-triangle-exclamation fa-3x"></i>
            </div>
            <div>
              <div className="fs-5 fw-bold">Hãy thử lại nhé</div>
              <div>
                Kết quả: <span className="warning fw-bold">{percent}%</span>
              </div>
            </div>
          </>
        )}
      </div>
      <div>
        <Button className="try-again-btn rounded-pill border-0" onClick={() => setAction("redo")}>
          Thử lại
        </Button>
      </div>
    </div>
  );
};

const Feedback = ({ status, explanation }) => {
  let icon, color, backgroundColor, title;

  if (status === 0) {
    title = "Sai rồi!";
    color = "#e9463b";
    backgroundColor = "#fef2f0";
    icon = <i className="fa-duotone fa-circle-xmark fa-2x"></i>;
  } else if (status === 2) {
    title = "Chưa đúng rồi!";
    color = "#808080";
    backgroundColor = "#f7f7f8";
    icon = <i className="fa-duotone fa-circle-exclamation fa-2x"></i>;
  } else {
    title = "Đúng rồi!";
    color = "#1DCD0E";
    backgroundColor = "#EFFAED";
    icon = <i className="fa-duotone fa-circle-check fa-2x"></i>;
  }
  return (
    <div className="feedback d-flex flex-row rounded-10 mt-4 p-4 pe-5" style={{ backgroundColor }}>
      <div style={{ color }}>{icon}</div>
      <div className={`ms-3 ${explanation ? "" : "d-flex align-items-center"}`}>
        <h5 className={`fw-bold ${explanation ? "" : "mb-0"}`} style={{ color }}>
          {title}
        </h5>
        <div className="lh-base fs-6" dangerouslySetInnerHTML={{ __html: explanation }} />
      </div>
    </div>
  );
};

export const Question = observer(
  forwardRef(({ question, index, isLast, seeResult = false }, ref) => {
    const store = useAppStore();
    const api = useAPI();
    const [editing, setEditing] = useState(false);
    // const userQuiz = store.currentLesson.quiz.userQuiz;
    let answers = [];

    useImperativeHandle(ref, () => ({
      getAnswerOfQuestion() {
        return answers;
      },
    }));

    const changeQuestionOrderHandler = (e, direction) => {
      e.stopPropagation();

      toastWithAsyncFetch(
        {
          loading: "Đang thay đổi thứ tự câu hỏi...",
          success: "Thay đổi thứ tự thành công!",
          error: "Thay dổi thứ tự thất bại!",
        },
        api.callApi.bind(null, "changeQuestionOrder", {
          params: { pk: question.id },
          body: { type: direction },
        }),
        () => {
          store.currentLesson.quiz.changeQuestionOrder(direction, index);
        },
      );
    };

    const showQuestionModal = (question) => {
      store.questionModal.setEntityId(question.id);
      store.questionModal.toggle();
    };

    const changeAnswerHandler = (e) => {
      let index = answers.findIndex((obj) => obj.answer_id === e.currentTarget.value);
      let answer = {
        question_id: question.id,
        answer_id: Number(e.currentTarget.value),
      };

      if (question.type === "single") {
        answers = [answer];
      } else {
        if (index >= 0) {
          delete answers[index];
        } else answers.push(answer);
      }
    };

    const checkSelectedAnswer = (answerId, type) => {
      let style, icon;
      const selected = question?.checkSelectedAnswer(answerId);

      if (selected === true) {
        style = "#1DCD0E";
        icon = type === "single" ? faCircleCheck : faSquareCheck;
      } else if (selected === false) {
        style = "#E9463B";
        icon = type === "single" ? faCircleCheck : faSquareCheck;
      } else {
        icon = type === "single" ? faCircle : faSquare;
      }

      return <FontAwesomeIcon icon={icon} size="lg" style={{ color: style }} />;
    };

    return (
      <>
        {editing && (
          <CreateQuestion
            questionData={question}
            isEditing={editing}
            questionHandler={(val) => setEditing(val)}
          />
        )}
        {!editing && (
          <div className="d-flex flex-row mb-4">
            <div className="question rounded-10 p-4 flex-grow-1">
              <div className="">
                <div className="d-flex flex-row justify-content-between align-items-center">
                  <h5 className="fw-bold lh-sm mb-1">Câu {index + 1}</h5>
                  {/* {!seeResult && (
                  <div className="point rounded fs-7 text-black-50 text-nowrap px-3 py-2">
                    {question.score} điểm
                  </div>
                )}
                {seeResult && ( */}
                  <div className="point rounded fs-7 text-black-50 text-nowrap px-3 py-2">
                    {question.is_correct === 1 || !seeResult ? question.score : "0"} điểm
                  </div>
                  {/* )} */}
                </div>
                <div
                  className="lh-base mt-3"
                  dangerouslySetInnerHTML={{ __html: question.content }}
                />
              </div>
              <div className="answers mt-2">
                {question.answers?.map((answer, index) => (
                  <div className="answer py-1" key={`answer-${index}`}>
                    {seeResult && (
                      <>
                        {checkSelectedAnswer(answer.id, question.type)}
                        <label className="ms-2">{answer.content}</label>
                      </>
                    )}
                    {!seeResult && (
                      <label htmlFor={`q-${question.id}a-${answer.id}`}>
                        <Form.Check
                          type={question.type === "single" ? "radio" : "checkbox"}
                          id={`q-${question.id}a-${answer.id}`}
                          value={answer.id}
                          name={`answer-${question.id}`}
                          label={answer.content}
                          onChange={changeAnswerHandler}
                        />
                      </label>
                    )}
                    {answer.image_url && (
                      <div className="d-flex my-2 px-4">
                        <div className="preview-img rounded-10 position-relative">
                          <img className="rounded-10" src={answer.image_url} width="300" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {seeResult && (
                <Feedback status={question.is_correct} explanation={question.explanation} />
              )}
            </div>

            {store.hasPermission("update_lesson") && (
              <div className="d-flex flex-column ms-2">
                <div
                  className="action d-flex rounded text-black-50 mb-2"
                  onClick={() => setEditing(true)}
                >
                  <i className="fa-regular fa-pen" />
                </div>
                <div
                  className="action d-flex rounded text-black-50 mb-2"
                  onClick={() => showQuestionModal(question)}
                >
                  <i className="fa-regular fa-trash" />
                </div>
                <div
                  className="action d-flex rounded text-black-50 mb-2"
                  disabled={index === 0}
                  onClick={(e) => changeQuestionOrderHandler(e, "up")}
                >
                  <i className="fa-solid px-1 fa-caret-up" />
                </div>
                <div
                  className="action d-flex rounded text-black-50"
                  disabled={isLast}
                  onClick={(e) => changeQuestionOrderHandler(e, "down")}
                >
                  <i className="fa-solid px-1 fa-caret-down" />
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  }),
);

export const CreateQuestionButton = ({ onClick }) => (
  <div
    role="button"
    className="create-question d-flex flex-row align-items-center rounded-10 p-3 text-black-50"
    onClick={onClick}
  >
    <i className="fa-duotone fa-circle-plus fa-lg me-3" />
    Thêm câu hỏi
  </div>
);

export const StartQuiz = ({ quiz, startQuiz }) => {
  return (
    <div className="bg-white rounded-10 p-4 flex-grow-1 mt-3">
      <div className="d-flex flex-column justify-content-between align-items-center mt-2">
        <h4 className="fw-bold mb-3">Thông tin</h4>
        <p className="text-center mb-1">
          Chỉ có thể <b>vượt qua</b> nếu tổng điểm bài làm đạt <b>từ 80%</b> trở lên.
        </p>
        <p className="text-center">
          Đối với <b>câu hỏi có nhiều lựa chọn</b>, chỉ khi phần trả lời <b>đúng 100%</b> thì mới
          được tính điểm.
        </p>
        {/* <p className="">
          <b>Thời gian làm bài:</b> {quiz.timing} phút
        </p> */}
        <p className="mb-1">
          <b>Số câu hỏi:</b> {quiz.questions.length} câu
        </p>
        <p>
          <b>Tổng điểm:</b> {quiz.total_score} điểm
        </p>

        <Button
          className="btn-success fw-bold rounded-pill border-0 px-5 py-3 mt-3"
          onClick={() => startQuiz(true)}
        >
          <i className="fa-solid fa-play me-2"></i> Bắt đầu
        </Button>
      </div>
    </div>
  );
};

export const Quiz = observer(() => {
  const store = useAppStore();
  const api = useAPI();
  const [createQuestion, setCreateQuestion] = useState(false);
  const [action, setAction] = useState("start");
  const [result, setResult] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const quiz = store.currentLesson.quiz;

  useEffect(() => {
    setAction("start");
    setResult(null);
  }, [quiz]);

  const refs = useMemo(() => {
    if (!quiz) {
      return null;
    }
    return quiz.questions.reduce((refArr, question) => {
      refArr[question.id] = React.createRef();
      return refArr;
    }, {});
  }, [quiz]);

  const submitHandler = () => {
    const answers = Object.values(refs)
      .map((ref) => ref.current?.getAnswerOfQuestion())
      .flat()
      .filter((answer) => (Array.isArray(answer) ? answer.length : answer));
    if (!answers.length) {
      toast.error("Vui lòng chọn câu trả lời.", TOASTIFY_OPTIONS);
      return;
    }

    setIsLoading(true);

    api
      .callApi("checkQuiz", {
        params: { pk: store.currentLesson.quiz.id },
        body: { answersData: answers },
      })
      .then((res) => {
        if (res.data.isPassed) {
          store.currentLesson.updateProgress(100);
        }
        setAction("done");
        setResult(res.data);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Xảy ra lỗi, không thể gửi câu trả lời.", TOASTIFY_OPTIONS);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const seeResultHandler = () => {
    setIsLoading(true);
    api
      .callApi("getResult", {
        params: { pk: quiz.id },
      })
      .then((res) => {
        store.currentLesson.quiz.update(res.data.data);
        setAction("see");
      })
      .catch((e) => {
        console.log(e);
        toast.error("Xảy ra lỗi, không thể xem trả lời.", TOASTIFY_OPTIONS);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const redoHandler = () => {
    setResult(null);
    setAction("start");
  };

  return (
    <>
      {action === "start" && !store.hasPermission("update_lesson") && (
        <StartQuiz quiz={quiz} startQuiz={() => setAction("doing")} disabled={isLoading || !quiz} />
      )}

      {result && action === "done" && (
        <Result
          result={result}
          isLoading={isLoading}
          setAction={(a) => (a === "see" ? seeResultHandler() : redoHandler())}
        />
      )}

      {result && action === "see" && (
        <div className="quiz d-flex flex-column mt-4">
          <Alert isPassed={result.isPassed} setAction={redoHandler} />
          {quiz?.questions.map((question, index) => (
            <Question key={index} question={question} index={index} seeResult={true} />
          ))}
        </div>
      )}

      {(action === "doing" || store.hasPermission("update_lesson")) && !result && (
        <div className="quiz d-flex flex-column mt-4">
          {quiz?.questions.map((question, index) => (
            <Question
              ref={refs[question.id]}
              key={index}
              question={question}
              index={index}
              isLast={quiz?.questions.length - 1 === index}
              seeResult={false}
            />
          ))}

          {createQuestion && <CreateQuestion questionHandler={(val) => setCreateQuestion(val)} />}

          {store.hasPermission("create_lesson") && !createQuestion && (
            <CreateQuestionButton onClick={() => setCreateQuestion(true)} />
          )}

          {action === "doing" && !store.hasPermission("update_lesson") && (
            <div className="d-flex justify-content-end">
              <Button
                className="submit-btn rounded-pill border-0"
                onClick={submitHandler}
                disabled={isLoading}
              >
                {isLoading ? <Spinner animation="border" size="sm" className="me-2" /> : ""}
                Nộp bài
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
});
